import { supabase } from '../config/supabase';

/**
 * API Client for making HTTP requests
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003/api';

// Flag to prevent multiple redirects to login
let isRedirectingToLogin = false;

/**
 * Generic request method with error handling
 * @param {string} endpoint - API endpoint
 * @param {object} options - Request options
 * @return {Promise} - API response
 */
const request = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // Get the current user's token
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || null;
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers,
  };
  
  const config = {
    ...options,
    headers,
  };
  
  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || 'An error occurred');
      error.status = response.status;
      error.data = errorData;
      
      // Handle unauthorized error
      if (response.status === 401 && !isRedirectingToLogin) {
        // Only redirect if not already on login/signup pages to prevent loops
        const currentPath = window.location.pathname;
        const authPaths = ['/login', '/signup', '/forgot-password', '/reset-password'];
        
        if (!authPaths.includes(currentPath)) {
          // Set flag to prevent multiple redirects
          isRedirectingToLogin = true;
          
          // Clear session and redirect
          try {
            await supabase.auth.signOut();
          } catch (signOutError) {
            console.error('Error signing out:', signOutError);
          }
          
          // Use replace to prevent back button loop
          window.location.replace('/login');
          return; // Prevent further execution
        }
      }
      
      throw error;
    }
    
    // Reset redirect flag on successful requests
    isRedirectingToLogin = false;
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed: ${error.message}`);
    throw error;
  }
};

/**
 * HTTP GET request
 * @param {string} endpoint - API endpoint
 * @param {object} options - Request options (can include params)
 * @return {Promise} - API response
 */
const get = (endpoint, options = {}) => {
  let url = endpoint;
  
  // Handle params option (convert to query string)
  if (options.params) {
    const queryParams = new URLSearchParams();
    Object.keys(options.params).forEach(key => {
      if (options.params[key] !== undefined && options.params[key] !== null) {
        queryParams.append(key, options.params[key]);
      }
    });
    const queryString = queryParams.toString();
    if (queryString) {
      url += (endpoint.includes('?') ? '&' : '?') + queryString;
    }
    
    // Remove params from options to avoid passing it to fetch
    const { params, ...restOptions } = options;
    options = restOptions;
  }
  
  return request(url, {
    ...options,
    method: 'GET',
  });
};

/**
 * HTTP POST request
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body
 * @param {object} options - Request options
 * @return {Promise} - API response
 */
const post = (endpoint, data, options = {}) => {
  return request(endpoint, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * HTTP PUT request
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body
 * @param {object} options - Request options
 * @return {Promise} - API response
 */
const put = (endpoint, data, options = {}) => {
  return request(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * HTTP PATCH request
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body
 * @param {object} options - Request options
 * @return {Promise} - API response
 */
const patch = (endpoint, data, options = {}) => {
  return request(endpoint, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

/**
 * HTTP DELETE request
 * @param {string} endpoint - API endpoint
 * @param {object} options - Request options
 * @return {Promise} - API response
 */
const del = (endpoint, options = {}) => {
  return request(endpoint, {
    ...options,
    method: 'DELETE',
  });
};

const apiClient = {
  get,
  post,
  put,
  patch,
  delete: del,
};

export default apiClient; 